import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Signal, SignalHigh, SignalLow, SignalMedium, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface GPSDiagnosticBadgeProps {
  pointsCollected: number;
  pointsRejected: number;
  currentAccuracy?: number;
  isWarmingUp: boolean;
}

const WARMUP_POINTS = 20; // Pontos necessários para calibração completa

export const GPSDiagnosticBadge = ({
  pointsCollected,
  pointsRejected,
  currentAccuracy,
  isWarmingUp
}: GPSDiagnosticBadgeProps) => {
  const totalPoints = pointsCollected + pointsRejected;
  const rejectionRate = totalPoints > 0 ? (pointsRejected / totalPoints) * 100 : 0;
  
  // Progresso da calibração (0-100%)
  const calibrationProgress = Math.min(100, (pointsCollected / WARMUP_POINTS) * 100);
  
  const getAccuracyColor = () => {
    if (!currentAccuracy) return "bg-muted";
    if (currentAccuracy < 20) return "bg-green-500";
    if (currentAccuracy < 40) return "bg-yellow-500";
    if (currentAccuracy < 60) return "bg-orange-500";
    return "bg-red-500";
  };
  
  const getAccuracyIcon = () => {
    if (!currentAccuracy) return <Signal className="w-3 h-3" />;
    if (currentAccuracy < 20) return <SignalHigh className="w-3 h-3" />;
    if (currentAccuracy < 60) return <SignalMedium className="w-3 h-3" />;
    return <SignalLow className="w-3 h-3" />;
  };

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex gap-2 flex-wrap items-center">
        {/* GPS Accuracy Badge */}
        <Badge 
          variant="secondary" 
          className={cn(
            "flex items-center gap-1",
            getAccuracyColor(),
            "text-white"
          )}
        >
          {getAccuracyIcon()}
          {currentAccuracy ? `${Math.round(currentAccuracy)}m` : "N/A"}
        </Badge>

        {/* Points Collected Badge */}
        <Badge variant="secondary" className="bg-blue-500 text-white">
          {pointsCollected} pontos
        </Badge>

        {/* High Rejection Rate Warning */}
        {rejectionRate > 30 && pointsCollected > 5 && (
          <Badge variant="destructive" className="animate-pulse">
            ⚠️ {Math.round(rejectionRate)}% rejeitados
          </Badge>
        )}
      </div>

      {/* Calibração em Progresso */}
      {isWarmingUp && (
        <div className="flex items-center gap-2 w-full">
          <Loader2 className="w-4 h-4 text-purple-500 animate-spin" />
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            Calibrando GPS...
          </span>
          <Progress 
            value={calibrationProgress} 
            className="flex-1 h-2"
          />
          <span className="text-xs text-muted-foreground">
            {Math.round(calibrationProgress)}%
          </span>
        </div>
      )}
      
      {/* Calibração Completa */}
      {!isWarmingUp && pointsCollected > 0 && (
        <div className="flex items-center gap-1">
          <span className="text-xs text-green-500">✓ GPS Calibrado</span>
        </div>
      )}
    </div>
  );
};
